import styled from "styled-components";
import { motion } from "framer-motion";
import { ArrowRight, Lock } from "lucide-react";
import { useEarlyAccess } from "../../context/EarlyAccessContext";

/* ================= SECTION ================= */

const Section = styled.section`
  position: relative;
  padding: 180px 20px;
  overflow: hidden;

  background: radial-gradient(
      900px at 18% 0%,
      rgba(245, 158, 11, 0.18),
      transparent 55%
    ),
    radial-gradient(700px at 82% 30%, rgba(56, 189, 248, 0.18), transparent 60%),
    ${({ theme }) => theme.colors.bg};

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.55),
      transparent 40%,
      rgba(0, 0, 0, 0.65)
    );
    pointer-events: none;
  }
`;

/* ================= CONTAINER ================= */

const Container = styled.div`
  max-width: 900px;
  margin: auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

/* ================= BADGE ================= */

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};

  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

/* ================= TEXT ================= */

const Heading = styled(motion.h2)`
  margin-top: 36px;
  font-size: clamp(38px, 5.5vw, 64px);
  font-weight: 1000;
  line-height: 1.05;
  letter-spacing: -1px;
  color: ${({ theme }) => theme.colors.text};
`;

const Sub = styled(motion.p)`
  margin: 28px auto 0;
  max-width: 720px;
  font-size: 19px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= CTA ================= */

const CTAButton = styled(motion.button)`
  margin-top: 52px;
  padding: 18px 44px;
  border-radius: 999px;
  border: none;
  cursor: pointer;

  font-size: 17px;
  font-weight: 900;
  color: #02040a;

  background: linear-gradient(135deg, #f59e0b, #facc15);

  display: inline-flex;
  align-items: center;
  gap: 12px;

  box-shadow: 0 0 70px rgba(245, 158, 11, 0.35);
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      120deg,
      rgba(255, 255, 255, 0.24),
      transparent 34%,
      transparent 68%,
      rgba(255, 255, 255, 0.12)
    );
    opacity: 0.7;
    pointer-events: none;
  }
`;

/* ================= FOOT NOTE ================= */

const Note = styled(motion.div)`
  margin-top: 26px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

/* ================= ANIMATIONS ================= */

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

/* ================= COMPONENT ================= */

export default function CTASection() {
  const { setOpen } = useEarlyAccess();
  return (
    <Section id="early-access">
      <Container>
        <Badge
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          <Lock size={14} />
          Early access is limited
        </Badge>

        <Heading
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          Build your career with
          <br />
          a roadmap you can actually follow
        </Heading>

        <Sub
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          Stop collecting advice. Start following one clear path with daily
          execution, visible progress, and support when consistency gets hard.
        </Sub>

        <CTAButton
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          whileHover={{
            scale: 1.04,
            y: -4,
            boxShadow: "0 0 90px rgba(245, 158, 11, 0.48)",
          }}
          whileTap={{ scale: 0.97 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 240, damping: 16 }}
          onClick={() => {
            setOpen(true);
          }}
        >
          Create My Free Roadmap
          <ArrowRight size={18} />
        </CTAButton>

        <Note
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          whileHover={{ y: -2, opacity: 1 }}
          viewport={{ once: true }}
        >
          <Lock size={14} />
          No spam. No pressure. You can leave anytime.
        </Note>
      </Container>
    </Section>
  );
}
